import React, { useEffect, useState, useCallback } from 'react'
import { post, get, obtainToken, bindUserSession } from '../api/auth'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { message } from 'antd'
import { useCustomisation } from '../hooks/useCustomisation'
import { DOCUMENT_ORIGIN } from '../api/consts'
import { AuthContext, AuthContextType } from './AuthContext'

type Links = {
  verify_code_url: string
  resend_code_url: string
  status_url: string
}

type AuthResult = {
  status?: string
  error?: string
  auth_token?: string
  _links: Links
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(null)
  const [emailConfirmationLinks, setEmailConfirmationLinks] =
    useState<Links | null>(null)
  const [user, setUser] = useLocalStorage('user', '')
  const [userId, setUserId] = useLocalStorage('userId', '')
  const { apiHost, realm } = useCustomisation()

  useEffect(() => {
    const authenticate = async () => {
      const envAuthToken = import.meta.env.VITE_AUTH_TOKEN

      if (envAuthToken) {
        // Use the token from environment variable
        setToken(envAuthToken)
      } else {
        // Fall back to the current process
        const currentToken = await obtainToken()
        if (currentToken) {
          setToken(currentToken.auth_token)
        }
      }
    }
    authenticate()
  }, [])

  const getBusinessPartner = useCallback(
    async (myToken) => {
      try {
        const result = await get(`https://${apiHost}/resource/v3/me`, myToken)
        if (result['business_partner']) {
          setUser(result['business_partner']['screenname'])
          setUserId(result['business_partner']['_id'])
        }
      } catch (error) {
        message.error(`Authentication failed:\n${error}`)
      }
    },
    [apiHost, setUser, setUserId]
  )

  useEffect(() => {
    if (token && apiHost) {
      getBusinessPartner(token)
    }
  }, [token, apiHost, getBusinessPartner])

  const verifyEmail = async (url, onSuccess, onError) => {
    try {
      if (!url) {
        onError('No URL provided')

        return false
      }
      const result: AuthResult = await get(url, token)

      if (emailVerificationNeeded(result['status'])) {
        setEmailConfirmationLinks(result['_links'])
        onError(result['status'])
      } else if (result['error']) {
        onError(result['error'])
      } else {
        if (result.auth_token) {
          // Attempt to bind user session
          const bindResult = await bindUserSession(result.auth_token)
          if (!bindResult) {
            onError('Failed to login. Please try again.')
            return false
          } else {
            setToken(result.auth_token)
          }
        }
        onSuccess(result)
      }
    } catch (error) {
      onError(error)
    }

    return true
  }

  const handleAuthRequest = async (url, values, onSuccess, onError) => {
    const newValues = { ...values, realm: realm }
    const result: AuthResult = await post(url, newValues)

    if (emailVerificationNeeded(result['status'])) {
      setEmailConfirmationLinks(result['_links'])
      onError(result['status'])
    } else if (result['error']) {
      onError(result['error'])
    } else {
      if (result.auth_token) {
        // Attempt to bind user session
        const bindResult = await bindUserSession(result.auth_token)
        if (!bindResult) {
          onError('Failed to login. Please try again.')
          return false
        } else {
          setToken(result.auth_token)
        }
      }
      onSuccess()
    }

    return true
  }

  const handleLogin = (values, onSuccess, onError) => {
    return handleAuthRequest(
      `https://${apiHost}/auth/auth`,
      values,
      onSuccess,
      onError
    )
  }

  const handleLogoutRequest = async () => {
    const url = `${DOCUMENT_ORIGIN}/rpc/users`
    const method = 'POST'

    const data = new URLSearchParams()
    data.append('method', 'logout')

    const response = await fetch(url, {
      method,
      body: data,
    })
    try {
      return await response.json()
    } catch (error) {
      console.error('Request failed:', error)
      throw error
    }
  }

  const handleRegister = (values, onSuccess, onError) => {
    return handleAuthRequest(
      `https://${apiHost}/auth/register`,
      values,
      onSuccess,
      onError
    )
  }
  const handleRequestNewPassword = (values, onSuccess, onError) => {
    return handleAuthRequest(
      `https://${apiHost}/auth/recover`,
      values,
      onSuccess,
      onError
    )
  }

  const handleRequestConfirmEmail = async (
    values,
    onSuccess,
    onError
  ): Promise<boolean> => {
    return verifyEmail(
      emailConfirmationLinks?.verify_code_url + values.code,
      onSuccess,
      onError
    )
  }

  const handleRequestResendCode = async (
    values,
    onSuccess,
    onError
  ): Promise<boolean> => {
    return verifyEmail(
      emailConfirmationLinks?.resend_code_url,
      onSuccess,
      onError
    )
  }

  const handleRequestCheckStatus = async (
    onSuccess,
    onError
  ): Promise<boolean> => {
    return verifyEmail(emailConfirmationLinks?.status_url, onSuccess, onError)
  }

  const getUserNames = () => {
    const parts = user.split(' ')
    let firstName = ''
    if (parts[0].endsWith('.')) {
      // like Dr.
      firstName = parts[1]
    } else {
      firstName = parts[0]
    }
    return { firstName, lastName: parts[parts.length - 1] }
  }
  const emailVerificationNeeded = (statusMsg) => {
    return statusMsg === 'email-verification'
  }

  const handleLogout = () => {
    handleLogoutRequest()
    setToken(null)
    setUser(null)
    setUserId(null)
  }

  const value: AuthContextType = {
    token,
    setToken,
    apiHost,
    user,
    getUserNames,
    userId,
    onLogin: handleLogin,
    onRegister: handleRegister,
    onRequestNewPassword: handleRequestNewPassword,
    onConfirmEmail: handleRequestConfirmEmail,
    onCheckStatus: handleRequestCheckStatus,
    onResendCode: handleRequestResendCode,
    onLogout: handleLogout,
    emailVerificationNeeded,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
